
import { useEffect, useState, Suspense, lazy } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import Loader from './Components/Loader';
import ErrorBoundary from './Components/ErrorBoundary';
// import SplashScreen from './Components/SplashScreen';
// import NoInternet from './Modal/NoInternet';

import { AlertProvider } from './Modal/AlertProviderModal';
import { CartProvider } from './Context/CartContext';

import { AuthProvider } from './Context/AuthProvider';
import MenuLayoutProvider from './Layouts/MenuLayout';
import { trackEvent, trackScreen } from './Services/analytics';
import { FavoriteProvider } from './Context/FavoriteContext';
import NotFound from './Components/NotFound';
import OldInvoicesPage from './Pages/Transactions/OldInvoices';
import DowntimePage from './Pages/Downtime/DowntimePage';
import DowntimeGuard from './Context/DowntimeGuard';
const LoginPage = lazy(() => import('./Pages/Login/LoginPage'));
const GetOTPPage = lazy(() => import('./Pages/Login/GetOtp'));
const LoginMPIN = lazy(() => import('./Pages/Login/LoginMPIN'));
const SetMpin = lazy(() => import('./Pages/Login/SetMPIN'));
const HomePage = lazy(() => import('./Pages/Home/HomePage'));

const SelectDealer = lazy(() => import('./Pages/Login/SelectDealer'));
const CartScreen = lazy(() => import('./Pages/Cart/CartScreen'));
const OrderPage = lazy(() => import('./Pages/Order/OrderPage'));
const ProductsView = lazy(() => import('./Pages/Products/ProductsView'));
const ProductsList = lazy(() => import('./Pages/Products/ProductsList'));
const ProductsLevel = lazy(() => import('./Pages/Products/ProductsLevel'));
const ProductsPage = lazy(() => import('./Pages/Products/ProductsPage'));
const SchemesPage = lazy(() => import('./Pages/Schemes/SchemesPage'));
const TransactionsPage = lazy(() => import('./Pages/Transactions/TransactionsPage'));
const SeeRecommended = lazy(() => import('./Pages/Schemes/SeeRecommended'));
const ProfilePage = lazy(() => import('./Pages/Profile/ProfilePage'));
const FavouritesPage = lazy(() => import('./Pages/Profile/Favourites'));
const InvoiceScreen = lazy(() => import('./Pages/Order/InvoiceScreen'));
const SearchPage = lazy(() => import('./Pages/Home/Search'));
const AddressBook = lazy(() => import('./Pages/Profile/AddressBook'));

// import { trackEvent } from './utils/analytics';

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const pathToScreenName = (pathname: string): string =>
  pathname
    .replace(/^\//, '')
    .replace(/\//g, ' > ')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase()) || 'Home';
const NavigationTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const screenName = pathToScreenName(location.pathname);
    const userId = localStorage.getItem('user_id') || undefined;
    const mobile = localStorage.getItem('mobile_number') || undefined;

    trackScreen(screenName, userId, mobile);
  }, [location.pathname]); // re-fires only when the route actually changes

  return null;
};

const AppContent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true); // splash screen
  const [loader, setLoader] = useState(false); // global overlay loader
  const [isConnected, setIsConnected] = useState(true);
  useEffect(() => {
    const initialize = async () => {

      // ── 2. Network check ───────────────────────────────────────────────
      const online = navigator.onLine;
      setIsConnected(online);

      if (online && isConnected) {
        await init();
      }

      // ── 3. Hide splash after minimum display time (matches RN 1400ms) ──
      setTimeout(() => setLoading(false), 1400);
    };

    initialize();

    // ── Network listeners (replaces NetInfo.addEventListener) ────────────
    const handleOnline = () => setIsConnected(true);
    const handleOffline = () => setIsConnected(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const init = async () => {
    setLoader(true);
    trackEvent('app_opened' );

    // ── Fetch app config / base URL (same as RN fetchAppVersionURL) ──────
    setLoader(false);
  };

  if (loading) {return <Loader loading />;}

  return (
    <ErrorBoundary>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        rtl={false}
        pauseOnFocusLoss
        closeButton={false}
        draggable
        pauseOnHover
      />

      <Suspense fallback={<Loader loading />}>
        <AlertProvider>
          <FavoriteProvider>
            <CartProvider>
              <NavigationTracker />
              {/* ── Single Routes tree ── */}
              <Routes>
              <Route element={<DowntimeGuard />}>

                {/* Public routes — no Auth needed */}
                <Route path="/" element={<LoginPage />} />
                <Route path="/getotp" element={<GetOTPPage />} />
                <Route path="/loginmpin" element={<LoginMPIN />} />
                <Route path="/setmpin" element={<SetMpin />} />

                {/* Protected routes — wrapped by Auth + Menu providers */}
                <Route
                  path="/*"
                  element={
                    <AuthProvider>
                      <MenuLayoutProvider>
                        <Routes>
                          <Route path="/selectdealer" element={<SelectDealer />} />
                          <Route path="/home" element={<HomePage />} />
                          <Route path="/search" element={<SearchPage navigate={navigate} />} />
                          <Route path="/profile" element={<ProfilePage />} />
                          <Route path="/addressbook" element={<AddressBook />} />
                          <Route path="/favorites" element={<FavouritesPage />} />
                          <Route path="/cart" element={<CartScreen />} />
                          <Route path="/orders" element={<OrderPage />} />
                          <Route path="/invoice" element={<InvoiceScreen />} />
                          <Route path="/schemes" element={<SchemesPage />} />
                          <Route path="/schemes/seerecommended" element={<SeeRecommended />} />
                          <Route path="/transactions" element={<TransactionsPage />} />
                          <Route path="/products" element={<ProductsPage />} />
                          <Route path="/productlevel" element={<ProductsLevel />} />
                          <Route path="/productlist" element={<ProductsList />} />
                          <Route path="/productview" element={<ProductsView />} />
                          <Route path="/old-invoices" element={<OldInvoicesPage />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </MenuLayoutProvider>
                    </AuthProvider>
                  }
                />
              </Route>
              </Routes>

              <Loader loading={loader} />

            </CartProvider>
          </FavoriteProvider>
        </AlertProvider>
      </Suspense>
    </ErrorBoundary>
  );
};

// ─────────────────────────────────────────────
// App — keep BrowserRouter in index.tsx/main.tsx
// ─────────────────────────────────────────────

function App() {
  return <AppContent />;
}

export default App;
